import { useSyncExternalStore } from 'react';
import { AnyAction } from 'redux';
import { DraftState } from '@/types';
import { Thunk } from '@/redux/types';
import { setDraftAction } from '../actions/setDraftAction';
import { DraftActionWithMemo } from '../types';
import { prepareProcessedImages } from './prepareProcessedImages';
import { withoutHistory } from './withoutHistory';

const history: DraftState[] = [];
const future: DraftState[] = [];
const draftHistorySubscribers: (() => void)[] = [];

let lastRequestAnimationFrameId: number;
const onHistoryChange = () => {
  cancelAnimationFrame(lastRequestAnimationFrameId);
  lastRequestAnimationFrameId = requestAnimationFrame(() => {
    const canRedo = future.length > 0;
    const canUndo = history.length > 0;

    if (canRedo !== lastDraftHistoryState[0] || canUndo !== lastDraftHistoryState[1]) {
      lastDraftHistoryState = [canRedo, canUndo];
      draftHistorySubscribers.forEach((callback) => callback());
    }
  });
};

export const addHistoryEntry = (action: DraftActionWithMemo, draft: DraftState) => {
  if (!action.noHistory) {
    history.push(draft);
    future.length = 0;
    onHistoryChange();
  }
};

export const redoAction: Thunk<Promise<void>, AnyAction> = async (dispatch, getState) => {
  const redoDraft = future.shift();
  if (redoDraft) {
    const { draft: currentDraft, draftDispatch } = getState();
    const nextDraft = await prepareProcessedImages(currentDraft, redoDraft);
    history.push(currentDraft);
    onHistoryChange();

    draftDispatch(withoutHistory(setDraftAction(nextDraft)));
  }
};

export const undoAction: Thunk<Promise<void>, AnyAction> = async (dispatch, getState) => {
  const undoDraft = history.pop();
  if (undoDraft) {
    const { draft: currentDraft, draftDispatch } = getState();
    const previousDraft = await prepareProcessedImages(currentDraft, undoDraft);
    future.unshift(currentDraft);
    onHistoryChange();

    draftDispatch(withoutHistory(setDraftAction(previousDraft)));
  }
};

let lastDraftHistoryState: [boolean, boolean] = [false, false];
const getDraftHistoryState = () => lastDraftHistoryState;

const subscribe = (callback: () => void) => {
  draftHistorySubscribers.push(callback);
  return () => draftHistorySubscribers.splice(draftHistorySubscribers.indexOf(callback), 1);
};
export const useUndoRedo = () => useSyncExternalStore(subscribe, getDraftHistoryState);
